import { IconX } from "@tabler/icons-react";
import ButtonStyled from "../../../components/common/ButtonStyled";
import { useFetchCountryWarehouseAllData } from "../../../apis/locationApis";
import SelectInput from "../../../components/Select";
import { FieldErrors, UseFormRegister } from "react-hook-form";
import TextareaField from "../../../components/common/TextareaField";
import NewSelectInput from "../../../components/NewSelectInput";
import { useLanguage } from "../../../context/LanguageContext";

type DeleteModalProps = {
  open: boolean;
  onClose: () => void;
  deleteOrder: () => void;
  isDeletePending: boolean;
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  clearErrors?: any;
  setValue?: any;
  watch?: any;
  inPlace?: boolean;
};

export default function DeleteModal({
  open,
  onClose,
  deleteOrder,
  isDeletePending,
  register,
  errors,
  clearErrors,
  setValue,
  watch,
  inPlace = false,
}: DeleteModalProps) {
  const { data: warehouses } = useFetchCountryWarehouseAllData();
  const { t } = useLanguage();

  if (!open) return null;

  if (inPlace) {
    return (
      <div className="bg-white/80 backdrop-blur-md border border-slate-200/80 shadow-sm rounded-2xl overflow-hidden flex flex-col">
        {/* Header */}

        <div className="flex justify-between items-center px-5 py-4 border-b border-slate-100 bg-white">
          <h3 className="text-lg font-bold text-[#212121]">{t("Delete")}</h3>
          <div className="flex items-center gap-2">
            <ButtonStyled
              value={t("Confirm")}
              onClick={deleteOrder}
              extraClasses="p-1.5 px-4 rounded-lg  text-xs"
              isPending={isDeletePending}
            />
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* Body */}

        <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 m-4 transition-all duration-300 space-y-3">
          <NewSelectInput
            name="warehouseId"
            label={t("Select Warehouse")}
            className="flex items-center gap-2"
            placeholder=""
            selectOptions={
              warehouses?.warehouses?.map((warehouse: any) => ({
                id: warehouse?.id,
                value: warehouse?.id,
                text: warehouse?.name,
              })) || []
            }
            register={register}
            clearErrors={clearErrors}
            validation={{ required: t("Warehouse is required") }}
            error={errors.warehouseId}
            disabled={!warehouses || warehouses?.warehouses?.length === 0}
            value={watch("warehouseId")}
            setValue={(val: string) => {
              setValue("warehouseId", val);
            }}
          />
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

          <TextareaField
            rows={2}
            className="flex items-center gap-2 rounded-lg"
            label={t("Comment")}
            register={register}
            name="comment"
            placeholder=""
            error={errors.comment}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
      <div className="rounded-2xl w-full max-w-[500px] h-[calc(100vh-2rem)] flex flex-col relative bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center flex-shrink-0">
          <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl">
            <h2 className="text-2xl font-bold">{t("Delete")}</h2>
          </div>
          <div className="relative">
            <button
              onClick={onClose}
              className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 mr-4 flex items-center justify-center flex-shrink-0 hover:bg-gray-300 transition"
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        {/* Body */}

        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl shadow-md space-y-4">
            <SelectInput
              name="warehouseId"
              label={t("Select Warehouse")}
              className="w-full"
              placeholder=""
              selectOptions={
                warehouses?.warehouses?.map((warehouse: any) => ({
                  id: warehouse?.id,
                  value: warehouse?.id,
                  text: warehouse?.name,
                })) || []
              }
              register={register}
              clearErrors={clearErrors}
              validation={{ required: t("Warehouse is required") }}
              error={errors.warehouseId}
              disabled={!warehouses || warehouses?.warehouses?.length === 0}
              value={watch("warehouseId")}
              setValue={(val: string) => {
                setValue("warehouseId", val);
              }}
            />

            <TextareaField
              rows={2}
              className="w-full"
              label={t("Comment")}
              register={register}
              name="comment"
              placeholder=""
              error={errors.comment}
            />
          </div>

          <div className="bg-white p-4 rounded-xl shadow-md flex flex-col justify-center items-center gap-2">
            <img
              src="/img/delete-product.png"
              alt=""
              className="h-24 w-auto opacity-90"
            />
            <p className="text-[#212121] font-semibold text-sm">
              {t("Are you sure want to Delete?")}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full sm:w-auto h-11 px-6 rounded-full border border-[#D8D8D8] bg-white hover:bg-[#F5F5F5] transition-all duration-200 font-medium cursor-pointer"
          >
            {t("Cancel")}
          </button>
          <ButtonStyled
            value={t("Confirm")}
            onClick={deleteOrder}
            extraClasses="w-full sm:w-auto min-w-[140px] h-11 px-6 rounded-full font-semibold bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl cursor-pointer"
            isPending={isDeletePending}
          />
        </div>
      </div>
    </div>
  );
}
